import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  CardActions,
  Chip,
} from '@mui/material';
import {
  LocalShipping,
  Security,
  SupportAgent,
  Star,
  Add as AddIcon,
} from '@mui/icons-material';
import { fetchProducts } from '../../store/slices/productSlice';
import { addToCart, addToLocalCart } from '../../store/slices/cartSlice';
import { RootState, Product } from '../../types';
import { AppDispatch } from '../../store/store';
import PriceDisplay from '../../components/common/PriceDisplay';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../utils/imageUtils';

interface FeatureItem {
  icon: React.ReactElement;
  title: string;
  description: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  
  const { products, loading } = useSelector((state: RootState) => state.products);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Fetch newest products on component mount
  useEffect(() => {
    dispatch(fetchProducts({ page: 0, size: 4, sortBy: 'createdAt', sortDirection: 'desc' }));
  }, [dispatch]);

  const handleAddToCart = (productId: number) => {
    const product = products.find((p: Product) => p.id === productId);
    if (!product) return;
    
    if (isAuthenticated) {
      // Use backend API for authenticated users
      dispatch(addToCart({ productId, quantity: 1 }));
    } else {
      // Use local sessionStorage for guest users
      dispatch(addToLocalCart({ product, quantity: 1 }));
    }
    
    toast.success(`Добавено в количката: ${product.title}`);
  };

  const features: FeatureItem[] = [
    {
      icon: <LocalShipping fontSize="large" />,
      title: t('home.features.freeShipping'),
      description: t('home.features.freeShippingDesc'),
    },
    {
      icon: <Security fontSize="large" />,
      title: t('home.features.securePayment'),
      description: t('home.features.securePaymentDesc'),
    },
    {
      icon: <SupportAgent fontSize="large" />,
      title: t('home.features.customerSupport'),
      description: t('home.features.customerSupportDesc'),
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          mb: 6,
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" component="div" gutterBottom sx={{ 
                textTransform: 'uppercase', 
                letterSpacing: 2, 
                opacity: 0.9,
                mb: 1
              }}>
                MATERIA MEDICA
              </Typography>
              <Typography variant="h2" component="h1" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                ХОМЕОПАТИЧНА ЛИТЕРАТУРА
              </Typography>
              <Typography variant="h6" paragraph sx={{ opacity: 0.9 }}>
                най-добра цена
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/shop')}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                {t('home.shopNow')}
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/images/ViennaDioscoridesPlant.jpg"
                alt="Homeopathic Medicine"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: 400,
                  objectFit: 'contain',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container>
        {/* Features Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
            {t('home.whyChooseUs')}
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card elevation={0} sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* New Books Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
            {t('home.newBooks')}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            {t('home.discoverNewBooks')}
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {products.slice(0, 4).map((product: Product) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => navigate(`/products/${product.id}`)}
                  >
                    <CardMedia
                      component="img"
                      height="240"
                      image={getImageUrl(product.imageUrl)}
                      alt={product.title}
                      sx={{ objectFit: 'contain' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom noWrap>
                        {product.title}
                      </Typography>
                      {product.author && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          by {product.author}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <PriceDisplay
                          price={product.price}
                          discountPrice={product.discountPrice}
                          priceEur={product.priceEur}
                          discountPriceEur={product.discountPriceEur}
                          variant="h6"
                          layout="stacked"
                        />
                        {product.averageRating && product.averageRating > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Star sx={{ color: 'warning.main', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ ml: 0.5 }}>
                              {product.averageRating.toFixed(1)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {product.categories?.[0] && (
                        <Chip
                          label={product.categories[0]?.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(product.id);
                        }}
                        disabled={product.stock === 0}
                      >
                        {product.stock === 0 ? t('home.outOfStock') : t('home.addToCart')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" size="large" onClick={() => navigate('/shop')}>
              {t('home.viewAllProducts')}
            </Button>
          </Box>
        </Box>


        {/* Mission Section */}
        <Box
          sx={{
            bgcolor: 'grey.800',
            color: 'white',
            borderRadius: 2,
            p: 6,
            mb: 8,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
            НАШАТА МИСИЯ
          </Typography>
          <Box sx={{ maxWidth: '900px', mx: 'auto', textAlign: 'left' }}>
            <Typography variant="body1" paragraph sx={{ mb: 3, lineHeight: 1.7 }}>
              Нашата мисия е да осигурим на практикуващите хомеопати, обучаващите се студенти по хомеопатия и на всички любители и ценители 
              на хомеопатичната специализирана литература, както на класици в хомеопатията, така и на съвременни автори.
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 3, lineHeight: 1.7 }}>
              Започнахме дейността си в период, когато на нашия пазар нямаше никаква литература на български език. Хомеопатите бяха принудени да 
              използват нужната им специализирана литература на чужд език, предимно английски и руски. Ние си поставихме амбициозната задача да 
              осигурим на българските хомеопати литературата, от която се нуждаят за ежедневната си дейност на роден език. Така информацията се 
              възприема много по-лесно и работата се улеснява.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
              Сега с гордост можем да кажем, че сме се справили със задачата, защото специализираната хомеопатична литература вече предлагаме на 
              своите ползватели, българските хомеопати, голям набор от различни справочници - Materia Medica, реперториуми, наръчници, учебна 
              литература, философия на хомеопатията, литература за шуслеровите соли и много други неща. Но ние няма да спрем, а продължаваме 
              мисията си. Очаквайте нови неща.
            </Typography>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default HomePage;